import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Save, Star, Pencil, X, ImagePlus } from "lucide-react";
import MediaUploader from "../components/MediaUploader";

/**
 * v2.47 — Testimonials now use the same MediaUploader as listings + projects.
 *
 *  - Multi-image gallery (drag-drop, mobile camera, reorder)
 *  - Multi-video walkthroughs (mp4/webm, 50MB cap)
 *  - First image auto-becomes the avatar (photo_url) — back-compat with old field
 *  - First video kept in legacy video_url field for back-compat with public site
 */

const empty = () => ({
  client_name: "",
  photo_url: "",
  video_url: "",
  images: [],
  videos: [],
  rating: 5,
  quote: "",
  deal_summary: "",
  location: "",
  enabled: true,
  sort_order: 0,
});

export default function AdminTestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const images = Array.isArray(form.images) ? form.images : [];
      const videos = Array.isArray(form.videos) ? form.videos : [];
      // Auto-sync legacy single fields so the public site keeps rendering correctly
      const photo_url = form.photo_url || images[0] || "";
      const video_url = form.video_url || videos[0] || "";
      const payload = {
        ...form,
        rating: Number(form.rating) || 5,
        sort_order: Number(form.sort_order) || 0,
        images,
        videos,
        photo_url,
        video_url,
      };
      if (editingId) {
        await api.patch(`/admin/testimonials/${editingId}`, payload);
        toast.success("Testimonial updated");
      } else {
        await api.post("/admin/testimonials", payload);
        toast.success("Testimonial added");
      }
      setForm(empty());
      setEditingId(null);
      await load();
    } catch (err) { toast.error(apiErrorMsg(err, "Save failed")); }
    setBusy(false);
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    // Coerce legacy data: if images/videos arrays are empty but legacy single fields exist, hydrate them
    const images = Array.isArray(t.images) && t.images.length
      ? t.images
      : (t.photo_url ? [t.photo_url] : []);
    const videos = Array.isArray(t.videos) && t.videos.length
      ? t.videos
      : (t.video_url ? [t.video_url] : []);
    setForm({ ...empty(), ...t, images, videos });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials-tab">
      <form onSubmit={submit} className="bg-white border border-[#E4E4E7] p-5 rounded-sm space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
            <Star size={16} /> {editingId ? "Edit Testimonial" : "Add Testimonial"}
          </h3>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={() => { setEditingId(null); setForm(empty()); }} data-testid="testimonial-cancel-edit">
              <X size={13} /> Cancel
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-3">
          <Field label="Client Name" required value={form.client_name} onChange={(v) => update("client_name", v)} testid="testimonial-name" />
          <Field label="Deal Summary (e.g. 2BHK Uttam Nagar @ ₹52L)" value={form.deal_summary} onChange={(v) => update("deal_summary", v)} testid="testimonial-deal" />
          <Field label="Location" value={form.location} onChange={(v) => update("location", v)} testid="testimonial-location" />
          <div>
            <Label>Rating</Label>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => update("rating", n)}
                  className="p-1"
                  data-testid={`testimonial-star-${n}`}
                >
                  <Star size={20} className={n <= (Number(form.rating) || 0) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D4D4D8]"} />
                </button>
              ))}
              <span className="ml-2 text-[12px] text-[#52525B] font-semibold">{form.rating}/5</span>
            </div>
          </div>
          <Field label="Sort order (lower = appears first)" type="number" value={form.sort_order} onChange={(v) => update("sort_order", v)} testid="testimonial-sort" />
        </div>

        <div>
          <Label>Quote *</Label>
          <Textarea required value={form.quote} onChange={(e) => update("quote", e.target.value)} rows={3} className="rounded-sm" placeholder="What the client said about us…" data-testid="testimonial-quote" />
        </div>

        {/* v2.47 — Modern media uploader (replaces URL textboxes) */}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-2 flex items-center gap-1.5">
            <ImagePlus size={11} /> Photos &amp; Video Walkthrough
          </div>
          <div className="text-[11px] text-[#52525B] mb-2">
            Pehli image automatically client avatar ban jayegi. Pehli video walkthrough section me dikhegi.
          </div>
          <MediaUploader
            images={form.images || []}
            videos={form.videos || []}
            onImagesChange={(next) => update("images", next)}
            onVideosChange={(next) => update("videos", next)}
            testidPrefix="testimonial"
          />
        </div>

        <label className="text-[12px] inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            data-testid="testimonial-enabled"
          />
          Enabled (publish to public site)
        </label>

        <Button type="submit" disabled={busy} className="btn-brand rounded-sm" data-testid="testimonial-submit">
          <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : (editingId ? "Save Changes" : "Add Testimonial")}
        </Button>
      </form>

      <div>
        <h3 className="font-heading font-semibold text-lg mb-3">Testimonials ({items.length})</h3>
        {items.length === 0 && <div className="text-[12px] text-[#A1A1AA] p-6 bg-white border border-[#E4E4E7] rounded-sm text-center">No testimonials yet.</div>}
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((t) => {
            const cover = (t.images && t.images[0]) || t.photo_url;
            return (
              <div key={t.id} className={`bg-white border rounded-sm p-4 ${t.enabled ? "border-[#E4E4E7]" : "border-[#FECACA] opacity-70"}`} data-testid={`testimonial-row-${t.id}`}>
                <div className="flex items-start gap-3">
                  {cover ? (
                    <img src={cover} alt={t.client_name} className="w-12 h-12 rounded-full object-cover" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#F4F4F5] flex items-center justify-center text-[#A1A1AA] text-lg font-semibold">{t.client_name?.[0]?.toUpperCase() || "?"}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-[14px]">{t.client_name}</div>
                    <div className="text-[11px] text-[#52525B]">{t.deal_summary}{t.location ? ` · ${t.location}` : ""}</div>
                    <div className="flex items-center gap-0.5 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={11} className={i < (t.rating || 0) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D4D4D8]"} />
                      ))}
                    </div>
                    <p className="text-[12px] text-[#09090B] mt-2 line-clamp-3 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10.5px] text-[#52525B]">
                      {(t.images || []).length > 0 && <span>📸 {t.images.length} photo{t.images.length !== 1 ? "s" : ""}</span>}
                      {(t.videos || []).length > 0 && <span>🎬 {t.videos.length} video{t.videos.length !== 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#F4F4F5]">
                  <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => startEdit(t)} data-testid={`testimonial-edit-${t.id}`}><Pencil size={11} /> Edit</Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-[#DC2626]" onClick={() => remove(t.id)} data-testid={`testimonial-del-${t.id}`}><Trash2 size={11} /> Delete</Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{children}</div>;
const Field = ({ label, value, onChange, type = "text", testid, required, placeholder }) => (
  <div>
    <Label>{label}</Label>
    <Input type={type} required={required} placeholder={placeholder || ""} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm" data-testid={testid} />
  </div>
);
